/**
 * The sidebar: right side, 1120px unless `width` says otherwise, over a scrim. Escape, the scrim and × close it;
 * `onBack` adds a labelled return link above the body.
 *
 * One shell for a person (<DrillThrough>) and a team (the Supervisors page), so the
 * two open, look and close the same way — the 2026-09-25 review asked for the
 * Supervisors page to "mirror exactly" what My team's metrics opens.
 */
import { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'

/** Panels currently mounted, oldest first. */
const open: symbol[] = []

/** Panels open now. An enlarged block (PopOut) leaves Esc to any opened over it. */
export const panelCount = () => open.length

/**
 * Fired when a panel that takes the reader somewhere opens — a person, a team. An
 * enlarged block (PopOut) closes on it, so the drill opens over the page rather than
 * behind a full-window layer. The comments drawer does not fire it: a reviewer
 * commenting on an enlarged table wants it still there.
 */
export const SIDE_PANEL_OPEN = 'cpc:side-panel-open'

const InPanel = createContext(false)

/**
 * True inside a panel. A block there does not offer Enlarge: the panel is already the
 * reader's focus, and Esc belongs to it.
 */
export const useInPanel = () => useContext(InPanel)

export function SidePanel({
  onClose,
  backLabel,
  onBack,
  label,
  width = 1120,
  hidden = false,
  zIndex = 50,
  keepsPopOut = false,
  children,
}: {
  onClose: () => void
  backLabel?: string
  onBack?: () => void
  /** What the panel shows, for screen readers. */
  label?: string
  /** Pixels; narrowed to 96vw on a small screen. */
  width?: number
  /**
   * Out of sight but still mounted, so drafts survive: the comments drawer steps
   * aside while a tab capture takes its frame.
   */
  hidden?: boolean
  /** Stacking; the comments drawer sits above a drill-through it was opened from. */
  zIndex?: number
  /** Leave an enlarged block (PopOut) open under this panel. */
  keepsPopOut?: boolean
  children: ReactNode
}) {
  // Escape closes the topmost panel only: the comments drawer can open over a
  // drill-through, and one keypress should not close both.
  const [id] = useState(() => Symbol('panel'))
  useEffect(() => {
    if (!keepsPopOut) window.dispatchEvent(new Event(SIDE_PANEL_OPEN))
    // Mount only: a panel does not change what it is while open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    open.push(id)
    return () => {
      open.splice(open.indexOf(id), 1)
    }
  }, [id])
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented && open[open.length - 1] === id) onClose()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose, id])

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(8,11,15,.52)',
        zIndex,
        display: 'flex',
        justifyContent: 'flex-end',
        visibility: hidden ? 'hidden' : undefined,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: `min(${width}px, 96vw)`,
          background: 'var(--plane)',
          height: '100%',
          overflow: 'auto',
          borderLeft: '1px solid var(--rule)',
        }}
      >
        <div style={{ padding: '18px 24px 60px' }}>
          {/* 1 — header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1 }}>
              {onBack && (
                <button
                  onClick={onBack}
                  style={{
                    background: 'none',
                    border: 0,
                    padding: 0,
                    marginBottom: 6,
                    cursor: 'pointer',
                    color: 'var(--accent-ink)',
                    fontSize: 'var(--t-table)',
                  }}
                >
                  ← Back to {backLabel}
                </button>
              )}
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 0,
                cursor: 'pointer',
                fontSize: 18,
                color: 'var(--ink-3)',
              }}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <InPanel.Provider value>{children}</InPanel.Provider>
        </div>
      </div>
    </div>
  )
}
